import { animate, style, transition, trigger } from '@angular/animations';
import { DatePipe, NgTemplateOutlet } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DOCUMENT,
  inject,
  OnDestroy,
  OnInit,
  signal,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { locationSuggestion } from '@components/form/components/form-layout-1/config';
import { Appointment, UserType } from '@components/form/components/form-layout-1/model';
import { IsStepVisiblePipe } from '@components/form/components/form-layout-1/pipe';
import { AppointmentService } from '@components/form/components/form-layout-1/services';
import { CacheService } from '@services/cache';
import { ElasticSearchService } from '@services/elastic-search';
import { LocalitySearch } from '@services/locality-search/models';
import { LogsService } from '@services/logs';
import { ResponsiveService } from '@services/responsive';
import { StoreLocatorService } from '@services/store-locator';
import { ButtonComponent } from '@shared/components/button';
import { DatePickerComponent } from '@shared/components/date-picker';
import { DropdownComponent } from '@shared/components/dropdown';
import { Dropdown } from '@shared/components/dropdown/model/dropdown';
import { FormItemComponent } from '@shared/components/form-item';
import { FormAbstractComponent } from '@shared/components/form-item/abstract';
import { FormItemListItem, ValueType } from '@shared/components/form-item/model';
import { MapService } from '@shared/components/map/services';
import { SearchComponent } from '@shared/components/search';
import { TitleComponent } from '@shared/components/title';
import { Title } from '@shared/components/title/model';
import { CustomError, Schedule } from '@shared/models';
import { WeekDay } from '@shared/types';
import { formatDateObjectAsLocal } from '@shared/utils';
import { debounceTime, EMPTY, map, Subject, switchMap, take, takeUntil } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';

interface DisplayBlock {
  suggestions: boolean;
  locationResults: boolean;
  proximityStores: boolean;
  storeNotFound: boolean;
  storeLoading: boolean;
}

const ALL_WEEK_DAYS = [0, 1, 2, 3, 4, 5, 6] as WeekDay[];

const commonSpecs = {
  name: {
    type: 'TEXT' as const,
    key: 'name',
    placeholder: 'saisir vos prénom et nom',
    autocomplete: 'off' as const,
    rules: {
      regexp: /^([A-Za-zÀ-ÖØ-öø-ÿ]+[-\s]?)+\s+([A-Za-zÀ-ÖØ-öø-ÿ]+[-\s]?)+$/,
      tips: 'Une information est manquante',
    },
  },
  phone: {
    type: 'TEXT' as const,
    key: 'phone',
    autocomplete: 'off' as const,
    placeholder: 'saisir votre numéro de téléphone',
    rules: {
      regexp: /^(0[1-9]\s?\d{2}\s?\d{2}\s?\d{2}\s?\d{2}|\+\d{11,})$/,
      tips: 'Numéro de téléphone invalide',
    },
  },
  email: {
    type: 'TEXT' as const,
    key: 'email',
    autocomplete: 'off' as const,
    placeholder: 'saisir votre adresse email',
    rules: {
      regexp: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
      tips: 'Adresse email invalide',
    },
  },
} as const;

@Component({
  selector: 'app-form-layout-1',
  imports: [
    FormItemComponent,
    IsStepVisiblePipe,
    ButtonComponent,
    SearchComponent,
    DropdownComponent,
    TitleComponent,
    DatePickerComponent,
    DatePipe,
    NgTemplateOutlet,
  ],
  templateUrl: './form-layout-1.component.html',
  styleUrl: './form-layout-1.component.scss',
  animations: [
    trigger('fadeInOut', [
      transition(':enter', [style({ opacity: 0 }), animate('0.2s ease-in', style({ opacity: 1 }))]),
    ]),
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FormLayout1Component extends FormAbstractComponent implements OnInit, OnDestroy {
  locationItems = signal<Dropdown[]>([]);
  locationAround = signal<Dropdown | null>(null);
  locationSuggestions = signal<Dropdown[]>(locationSuggestion);
  storeSuggestions = signal<Dropdown[]>([]);
  storeSelected = signal('');
  isTypeSelected = signal(false);
  locationSelected = signal('');
  locationValueInput = signal('');
  isDisplay = signal<DisplayBlock>({
    suggestions: true,
    storeNotFound: false,
    locationResults: false,
    proximityStores: false,
    storeLoading: false,
  });
  readonly title = signal<Title>({
    level: 'H1',
    text: 'Parlons de votre projet, vos envies.',
    size: {
      mobile: 'H1',
      desktop: 'H1',
    },
    color: 'WHITE',
  });
  error = signal('');
  dialogDateIsOpened = signal(false);
  isGeolocaliseLoading = signal(false);
  isLoading = signal(false);
  isSendFailed = signal(false);
  dateStart = signal<Date | null>(null);
  dateEnd = signal<Date | null>(null);
  storeUuid = signal<string | undefined>(undefined);
  storePhoneNumber = signal<string | undefined>(undefined);
  storeHomeAppointmentDays = signal<WeekDay[] | null>(null);
  storeClosedDays = signal<WeekDay[]>([]);
  isRDVDOM = signal<boolean>(false);
  resetTypeForm = signal(0);
  resetFormTrigger = signal(0);
  rdvList = signal<FormItemListItem[]>([]);
  proRdvList = signal<FormItemListItem[]>([]);
  userType = signal<UserType>('particulier');
  specs = computed(() => ({
    ...commonSpecs,
    project: {
      type: 'SEARCH' as const,
      key: 'project',
      placeholder: 'sélectionner un type de projet',
      list: [
        { key: 'RDV internet - plusieurs pieces', label: 'plusieurs pièces' },
        { key: 'RDV Internet - cuisine', label: 'une cuisine' },
        { key: 'RDV internet - sdb', label: 'une salle de bain' },
        { key: 'RDV internet - dressing', label: 'un dressing' },
        { key: 'RDV internet - sur mesure', label: 'un meuble sur mesure' },
      ],
    },
    type: {
      type: 'SEARCH' as const,
      key: 'type',
      placeholder: 'choisir un type de RDV',
      list: this.rdvList(),
    },
    addressStreet: {
      type: 'TEXT' as const,
      key: 'address',
      autocomplete: 'off' as const,
      placeholder: 'saisir votre adresse',
    },
    zipcode: {
      type: 'TEXT' as const,
      key: 'zipcode',
      autocomplete: 'off' as const,
      placeholder: 'code postal',
      rules: {
        regexp: /^\d{5}$/,
        tips: 'Code postal invalide',
      },
    },
    city: {
      type: 'TEXT' as const,
      key: 'city',
      autocomplete: 'off' as const,
      placeholder: 'ville',
    },
    optin: {
      type: 'BOOLEAN' as const,
      key: 'optinEmail',
      label:
        "Je souhaite m'abonner à la newsletter Perene et recevoir chaque trimestre, de l'inspiration dans ma boîte mail.",
    },
  }));
  proSpecs = computed(() => ({
    ...commonSpecs,
    company: {
      type: 'TEXT' as const,
      key: 'company',
      autocomplete: 'off' as const,
      placeholder: 'saisir le nom de votre entreprise',
    },
    project: {
      type: 'TEXT' as const,
      key: 'proProject',
      autocomplete: 'off' as const,
      placeholder: 'décrire votre projet',
    },
    type: {
      type: 'SEARCH' as const,
      key: 'type',
      placeholder: 'choisir un type de RDV',
      list: this.proRdvList(),
    },
    optin: {
      type: 'BOOLEAN' as const,
      key: 'optinEmail',
      label: "Je souhaite m'abonner à la newsletter trimestrielle Perene à destination des professionnels",
    },
  }));
  availableDays = computed<WeekDay[]>(() => {
    const storeHomeAppointmentDays = this.storeHomeAppointmentDays();
    const isRDVDOM = this.isRDVDOM();
    const closedDays = this.storeClosedDays();

    const baseDays = !storeHomeAppointmentDays || !isRDVDOM ? ALL_WEEK_DAYS : storeHomeAppointmentDays;
    return baseDays.filter((day) => !closedDays.includes(day));
  });
  readonly breakpoints = inject(ResponsiveService).breakpoints;
  readonly #document = inject<Document>(DOCUMENT);
  readonly #window = this.#document.defaultView as Window;
  readonly #router = inject(Router);
  readonly #route = inject(ActivatedRoute);
  readonly #mapService = inject(MapService);
  isGoogleApiLoaded = this.#mapService.isLoaded;
  readonly #elasticSearchService = inject(ElasticSearchService);
  readonly #appointmentService = inject(AppointmentService);
  readonly #cacheService = inject(CacheService);
  readonly #logsService = inject(LogsService);
  readonly #searchInputValue: Subject<string> = new Subject();
  private readonly storeLocatorService = inject(StoreLocatorService);
  private readonly destroy$: Subject<boolean> = new Subject<boolean>();
  #locationResults: LocalitySearch[] = [];

  constructor() {
    super();
    this.#mapService.load();

    this.#searchInputValue
      .pipe(
        debounceTime(100),
        switchMap((value: string) => this.storeLocatorService.getLocation(value)),
        takeUntil(this.destroy$)
      )
      .subscribe((locations) => {
        this.#locationResults = locations ?? [];

        if (!locations || locations.length === 0) {
          this.locationItems.set([]);
          return;
        }

        this.locationItems.set(
          locations.map((location) => ({
            key: location.id,
            label: location.name,
          }))
        );
      });
  }

  ngOnInit(): void {
    const store = this.#route.snapshot.queryParamMap.get('store');
    const storeLabel = this.#route.snapshot.queryParamMap.get('label');
    const storeSpecific = this.#route.snapshot.queryParamMap.get('m');
    const isHomeAppointment = this.#route.snapshot.queryParamMap.get('isHomeAppointment');
    const phone = this.#route.snapshot.queryParamMap.get('phone');
    if (storeSpecific) {
      void this.#router.navigate([], {
        queryParams: { m: null },
        queryParamsHandling: 'merge',
        replaceUrl: true,
      });
      this.#elasticSearchService
        .getStores()
        .pipe(
          take(1),
          map((stores) => stores.find((store) => store.url.replace('/magasin/', '') === storeSpecific)),
          tap((store) => {
            if (store) {
              this.storeSelected.set(store.name);
              this.storeUuid.set(store.uuid);
              this.storePhoneNumber.set(store.phone);
              this.storeHomeAppointmentDays.set(store.homeAppointmentDays ?? ALL_WEEK_DAYS);
              this.storeClosedDays.set(store.closedDays);
              this.buildRdvTypeList(isHomeAppointment === String(store.isHomeAppointment));
              this.updateForm({ key: 'location', value: store.uuid, isValid: true });
            }
          })
        )
        .subscribe({
          error: (err: unknown) =>
            this.#logsService.error('FormLayout1Component', 'Store preselection failed (m param)', err),
        });
    }
    if (store && storeLabel && isHomeAppointment && phone) {
      void this.#router.navigate([], {
        queryParams: { store: null, label: null, isHomeAppointment: null, phone: null },
        queryParamsHandling: 'merge',
        replaceUrl: true,
      });
      this.storeSelected.set(storeLabel);
      this.storeUuid.set(store);
      this.storePhoneNumber.set(phone);
      this.buildRdvTypeList(isHomeAppointment === 'true');
      this.updateForm({ key: 'location', value: store, isValid: true });
      this.#elasticSearchService
        .getStores()
        .pipe(
          take(1),
          map((stores) => stores.find((s) => s.uuid === store)),
          tap((s) => {
            if (s) {
              this.storeHomeAppointmentDays.set(s.homeAppointmentDays ?? ALL_WEEK_DAYS);
              this.storeClosedDays.set(s.closedDays);
            }
          })
        )
        .subscribe({
          error: (err: unknown) =>
            this.#logsService.error('FormLayout1Component', 'Store schedule enrichment failed', err),
        });
    } else if (store) {
      this.#elasticSearchService
        .getStores()
        .pipe(
          take(1),
          map((stores) => stores.find((s) => s.uuid === store)),
          tap((s) => {
            if (s) {
              this.storeSelected.set(s.name);
              this.storeUuid.set(s.uuid);
              this.storePhoneNumber.set(s.phone);
              this.storeHomeAppointmentDays.set(s.homeAppointmentDays ?? ALL_WEEK_DAYS);
              this.storeClosedDays.set(s.closedDays);
              this.buildRdvTypeList(isHomeAppointment === String(s.isHomeAppointment));
              this.updateForm({ key: 'location', value: s.uuid, isValid: true });
            }
          })
        )
        .subscribe({
          error: (err: unknown) =>
            this.#logsService.error('FormLayout1Component', 'Store preselection failed (store param)', err),
        });
    }
    this.locationAround.set({
      key: 'geoloc',
      label: 'Autour de vous',
      icon: 'PIN',
    });
  }

  onAroundMeClick(): void {
    if (this.isGeolocaliseLoading()) return;
    this.isGeolocaliseLoading.set(true);
    this.#mapService
      .getPosition$()
      .pipe(
        takeUntil(this.destroy$),
        tap((value) => {
          if (value.status === 'OK') {
            this.locationSelected.set(`${value.address.locality} (${value.address.postal_code})`);
            this.isDisplay.update((current) => ({ ...current, suggestions: false }));
            this.locationItems.set([]);
            this.getStores(value.lat, value.lng);
            this.locationAround.set(null);
          } else if (value.status === 'KO') {
            this.error.set(value.error);
          }
          this.isGeolocaliseLoading.set(false);
        }),
        catchError((error: GeolocationPositionError) => {
          this.#logsService.debug('FormLayout1Component', 'Geolocation failed', {
            code: error.code,
            message: error.message,
          });
          this.isGeolocaliseLoading.set(false);
          this.error.set('Vous devez activer la localisation pour pouvoir être localisé.');
          return EMPTY;
        })
      )
      .subscribe();
  }

  onLocationValueInput(value: string): void {
    this.isDisplay.update((current) => ({ ...current, proximityStores: false, storeNotFound: false }));
    if (value.length === 0) {
      this.locationSelected.set('');
      this.storeSuggestions.set([]);
      this.locationAround.set({
        key: 'geoloc',
        label: 'Autour de vous',
        icon: 'PIN',
      });
      this.isDisplay.update((current) => ({ ...current, suggestions: true }));
    }
    if (value.length > 2) {
      this.locationValueInput.set(value);
      this.#searchInputValue.next(value);
      this.isDisplay.update((current) => ({ ...current, suggestions: false }));
    } else {
      this.locationValueInput.set('');
      this.locationItems.set([]);
      this.isDisplay.update((current) => ({ ...current, suggestions: true }));
    }
  }

  onLocationSelected($event: Dropdown): void {
    this.locationAround.set(null);
    this.isDisplay.update((current) => ({ ...current, suggestions: false }));
    this.locationItems.set([]);
    // The static shortcuts (locationSuggestion) carry their own coordinates; search results do not.
    if ($event['zipcode']) {
      this.locationSelected.set(`${$event.label} (${$event['zipcode'] as string})`);
      this.getStoreSuggestions($event);
      return;
    }

    const selected = this.#locationResults.find((location) => location.id === $event.key);
    if (!selected) {
      this.#logsService.error('FormLayout1Component', 'Selected location is missing from the last search results');
      return;
    }

    this.storeLocatorService
      .resolveCoordinates(selected)
      .pipe(takeUntil(this.destroy$))
      .subscribe((coordinates) => {
        if (coordinates?.lat === undefined || coordinates.lng === undefined) {
          this.#logsService.warn('FormLayout1Component', 'No coordinates resolved for the selected location');
          return;
        }
        this.locationSelected.set(selected.name);
        this.getStores(coordinates.lat, coordinates.lng);
      });
  }

  onStoreSelected($event: Dropdown): void {
    this.buildRdvTypeList($event['isHomeAppointment'] as boolean);
    this.dateStart.set(null);
    this.dateEnd.set(null);
    this.storeSelected.set(String($event['name']));
    this.storePhoneNumber.set(String($event['phone']));
    this.storeUuid.set(String($event['key']));
    const homeAppointmentDaysStr = $event['homeAppointmentDays'] as string | undefined;
    let homeAppointmentDays: WeekDay[] | undefined;
    if (homeAppointmentDaysStr) {
      try {
        homeAppointmentDays = JSON.parse(homeAppointmentDaysStr) as WeekDay[];
      } catch (e) {
        this.#logsService.error('FormLayout1Component', 'Error parsing homeAppointmentDays', e);
        homeAppointmentDays = ALL_WEEK_DAYS;
      }
    }

    this.storeHomeAppointmentDays.set(homeAppointmentDays ?? ALL_WEEK_DAYS);
    const closedDaysStr = $event['closedDays'] as string | undefined;
    let closedDays: WeekDay[] = [];
    if (closedDaysStr) {
      try {
        closedDays = JSON.parse(closedDaysStr) as WeekDay[];
      } catch (e) {
        this.#logsService.error('FormLayout1Component', 'Error parsing closedDays', e);
      }
    }
    this.storeClosedDays.set(closedDays);
    this.updateForm({ key: 'dateStart', value: null, isValid: false });
    this.updateForm({ key: 'dateEnd', value: null, isValid: false });
    this.updateForm({ key: 'location', value: String($event['key']), isValid: true });
  }

  ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.complete();
  }

  openStoreDialog(dialog: HTMLDialogElement): void {
    dialog.showModal();
    const searchInput: HTMLInputElement | null = this.#document.querySelector('.search-input > input');
    if (searchInput) {
      searchInput.focus();
    }
  }

  openDateDialog(
    dialogDate: HTMLDialogElement,
    dialogAskStore: HTMLDialogElement,
    dialogAskType: HTMLDialogElement
  ): void {
    if (!this.storeSelected()) {
      dialogAskStore.showModal();
      return;
    } else if (!this.isTypeSelected()) {
      dialogAskType.showModal();
      return;
    }
    dialogDate.showModal();
    this.dialogDateIsOpened.set(true);
  }

  closeDateDialog(dialog: HTMLDialogElement): void {
    dialog.close();
    this.dialogDateIsOpened.set(false);
  }

  closeLocationDialog(location: HTMLDialogElement): void {
    location.close();
  }

  onSelectedDate(dateSelected: { day: Date; schedule: Schedule }): void {
    const year = dateSelected.day.getFullYear();
    const month = dateSelected.day.getMonth();
    const date = dateSelected.day.getDate();
    const hoursStart = parseInt(dateSelected.schedule.start.slice(0, 2), 10);
    const minutesStart = parseInt(dateSelected.schedule.start.slice(3, 5), 10);
    const hoursEnd = parseInt(dateSelected.schedule.end.slice(0, 2), 10);
    const minutesEnd = parseInt(dateSelected.schedule.end.slice(3, 5), 10);
    const seconds = dateSelected.day.getSeconds();
    const milliseconds = dateSelected.day.getMilliseconds();
    const dateStart = new Date(year, month, date, hoursStart, minutesStart, seconds, milliseconds);
    const dateEnd = new Date(year, month, date, hoursEnd, minutesEnd, seconds, milliseconds);
    this.dateStart.set(dateStart);
    this.dateEnd.set(dateEnd);
    this.updateForm({ key: 'dateStart', value: dateStart, isValid: true });
    this.updateForm({ key: 'dateEnd', value: dateEnd, isValid: true });
  }

  onSuccess() {
    if (this.isLoading()) return;
    if (this.isValidForm()) {
      this.isLoading.set(true);
      this.isSendFailed.set(false);
      const isPro = this.userType() === 'professionnel';
      const form = this.form as {
        name: string;
        phone: string;
        email: string;
        type: { key: string };
        project?: { key: string; label: string };
        proProject?: string;
        location: string;
        dateStart: Date;
        dateEnd: Date;
        address: string;
        city: string;
        zipcode: string;
        optinEmail?: boolean;
        company?: string;
      };
      const projectLabel = isPro ? form.proProject! : form.project!.label;
      const osirisTitle = isPro ? 'PRO RDV' : form.project!.key;
      const nameParts: string[] = form.name.split(' ');
      let toSaved: Appointment = {
        name: nameParts.slice(1).join(' '),
        firstName: nameParts[0]!,
        phone: form.phone.replace(/\s+/g, ''),
        email: form.email,
        message: `Bonjour, je voudrais prendre RDV pour étudier mon projet concernant ${projectLabel}.`,
        project: projectLabel,
        osirisTitle,
        type: form.type.key,
        start: formatDateObjectAsLocal(form.dateStart)!,
        end: formatDateObjectAsLocal(form.dateEnd)!,
        optinEmail: !!form.optinEmail,
        store: `/stores/${form.location}`,
        ...(isPro && form.company ? { company: form.company } : {}),
      };
      // do not log the full payload (visitor PII): business fields only
      this.#logsService.debug('FormLayout1Component', 'Appointment data to save:', {
        store: toSaved.store,
        type: toSaved.type,
        osirisTitle: toSaved.osirisTitle,
        start: toSaved.start,
        end: toSaved.end,
      });
      if (this.#getIsRDVDOM()) {
        toSaved = {
          ...toSaved,
          address: form.address,
          city: form.city,
          zipcode: form.zipcode,
        };
      }
      this.#appointmentService
        .create(toSaved, true)
        .pipe(
          take(1),
          tap(() => {
            // catch-then order makes the confirmation navigation unconditional even if the cache write fails
            void this.#cacheService
              .set('form-layout-1', {
                store: this.storeSelected(),
                date: form.dateStart,
              })
              .catch((error: unknown) => {
                this.#logsService.error(
                  'FormLayout1Component',
                  'Failed to cache appointment recap before confirmation',
                  error
                );
              })
              .then(() => {
                const confirmationSlug = isPro ? 'confirmation-pro' : 'confirmation';
                void this.#router.navigate([`${this.#window.location.pathname}/${confirmationSlug}`]);
              });
          }),
          catchError((err: unknown) => {
            // err is the ResponseInterceptor wrapper { server: HttpErrorResponse, error, status } (CustomError).
            // Never log toSaved (visitor PII incl. home address); skip err.error too (it may echo form fields).
            const { status, server } = err as Partial<CustomError>;
            this.#logsService.error('FormLayout1Component', 'Error while saving appointment', {
              status,
              url: server?.url,
              store: toSaved.store,
              type: toSaved.type,
              osirisTitle: toSaved.osirisTitle,
              start: toSaved.start,
            });
            this.isLoading.set(false);
            this.isSendFailed.set(true);
            return EMPTY;
          })
        )
        .subscribe();
    }
  }

  override updateForm(data: { key: string; value: ValueType; isValid: boolean }): void {
    super.updateForm(data);
    if (data.key === 'type' && data.isValid) {
      this.isTypeSelected.set(true);
      const isRDVDOM = this.#getIsRDVDOM();
      this.isRDVDOM.set(isRDVDOM);
      this.#resetDate();
    }

    if (data.key === 'location' && this.isTypeSelected()) {
      this.#resetType();
    }
  }

  onReset(): void {
    this.isDisplay.update((current) => ({ ...current, storeLoading: false }));
  }

  selectUserType(type: UserType): void {
    if (this.userType() === type) return;
    this.userType.set(type);
    this.form = {};
    this.errorFormArray.clear();
    this.isValidForm.set(false);
    this.storeSelected.set('');
    this.storeUuid.set(undefined);
    this.storePhoneNumber.set(undefined);
    this.storeHomeAppointmentDays.set(null);
    this.storeClosedDays.set([]);
    this.dateStart.set(null);
    this.dateEnd.set(null);
    this.isTypeSelected.set(false);
    this.isRDVDOM.set(false);
    this.resetTypeForm.update((v) => v + 1);
    this.resetFormTrigger.update((v) => v + 1);
    this.locationSelected.set('');
    this.storeSuggestions.set([]);
    this.rdvList.set([]);
    this.proRdvList.set([]);
    this.isDisplay.set({
      suggestions: true,
      storeNotFound: false,
      locationResults: false,
      proximityStores: false,
      storeLoading: false,
    });
  }

  checkStoreSelected(dialogAskStore: HTMLDialogElement) {
    if (!this.storeSelected()) dialogAskStore.showModal();
  }

  closeAskStoreDialog(dialog: HTMLDialogElement) {
    dialog.close();
  }

  #resetDate() {
    if (this.dateStart() || this.dateEnd()) {
      this.#logsService.debug('FormLayout1Component', 'Reset dateStart and dateEnd');
      this.dateStart.set(null);
      this.dateEnd.set(null);
      this.updateForm({ key: 'dateStart', value: null, isValid: false });
      this.updateForm({ key: 'dateEnd', value: null, isValid: false });
    }
  }

  #resetType() {
    if (this.isTypeSelected()) {
      this.#logsService.debug('FormLayout1Component', 'Reset type');
      this.resetTypeForm.update((v) => v + 1);
      this.isRDVDOM.set(false);
      this.isTypeSelected.set(false);
    }
  }

  #getIsRDVDOM(): boolean {
    const type = this.form?.['type'];
    return !!type && typeof type === 'object' && 'key' in type && (type.key === 'RDVDOM' || type.key === 'RDVPROLOC');
  }

  private getStores(lat: number, lon: number): void {
    this.isDisplay.update((current) => ({ ...current, storeLoading: true }));
    this.#elasticSearchService
      .getNearestStores({ lat, lon })
      .pipe(
        takeUntil(this.destroy$),
        tap((stores) => {
          this.storeSuggestions.set(
            stores.map((store) => ({
              key: store.uuid,
              label: `${store.name}, ${store.address}`,
              name: store.name,
              address: store.address,
              isHomeAppointment: store.isHomeAppointment,
              phone: store.phone,
              homeAppointmentDays: store.homeAppointmentDays ? JSON.stringify(store.homeAppointmentDays) : undefined,
              closedDays: store.closedDays.length > 0 ? JSON.stringify(store.closedDays) : undefined,
            }))
          );
          if (this.storeSuggestions().length > 0) {
            this.isDisplay.update((current) => ({
              ...current,
              proximityStores: true,
              storeNotFound: false,
              storeLoading: false,
            }));
          } else {
            this.isDisplay.update((current) => ({
              ...current,
              proximityStores: false,
              storeNotFound: true,
              storeLoading: false,
            }));
          }
        })
      )
      .subscribe({
        error: (err: unknown) => {
          this.isDisplay.update((current) => ({ ...current, storeLoading: false, storeNotFound: true }));
          this.#logsService.error('FormLayout1Component', 'Nearest stores search failed', err);
        },
      });
  }

  private buildRdvTypeList(isHomeAppointment: boolean): void {
    this.rdvList.set([
      { key: 'RDVINT', label: 'vous accueillir en boutique' },
      { key: 'RDVVISIO', label: 'vous rencontrer en visio' },
    ]);
    this.proRdvList.set([
      { key: 'RDVPROMAG', label: 'vous accueillir en boutique' },
      { key: 'RDVPROVIS', label: 'vous rencontrer en visio' },
    ]);
    if (isHomeAppointment) {
      this.rdvList.update((current) => [...current, { key: 'RDVDOM', label: 'se déplacer à votre domicile' }]);
      this.proRdvList.update((current) => [...current, { key: 'RDVPROLOC', label: 'se déplacer dans vos locaux' }]);
    }
  }

  private getStoreSuggestions(city: Dropdown): void {
    this.locationAround.set(null);
    this.getStores(city['lat'] as number, city['lng'] as number);
  }
}
